import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Plus, FileUp, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';

interface CustomerPo {
  id: string;
  poNumber: string;
  customerName: string;
  status: string;
  totalAmount: number;
  poDate: string;
  requestedDelivery?: string;
}

export function CustomerPosPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Fetch customer POs
  const { data: pos, isLoading } = useQuery({
    queryKey: ['customer-pos', sessionId],
    queryFn: async () => {
      const result = await api.get<{ pos: CustomerPo[] }>('/customer-pos', sessionId);
      return result.pos;
    },
    enabled: !!sessionId,
  });

  // Filter POs
  const filteredPos = (pos || []).filter(po => {
    if (searchValue) {
      const q = searchValue.toLowerCase();
      const matches =
        po.poNumber.toLowerCase().includes(q) ||
        po.customerName.toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (statusFilter !== 'all' && po.status !== statusFilter) return false;
    return true;
  });

  const handleRowClick = (poId: string) => {
    setLocation(`/customer-pos/${poId}`);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer POs</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage purchase orders from customers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setLocation('/customer-pos/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Customer PO
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search PO numbers or customers..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="fulfilled">Fulfilled</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">
              Loading customer POs...
            </div>
          ) : filteredPos.length === 0 ? (
            <div className="text-center py-12">
              <FileUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No customer POs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PO #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PO Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requested Delivery
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPos.map((po) => (
                    <tr
                      key={po.id}
                      onClick={() => handleRowClick(po.id)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {po.poNumber}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {po.customerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={
                          po.status === 'fulfilled' ? 'default' :
                          po.status === 'processing' ? 'secondary' :
                          po.status === 'cancelled' ? 'destructive' :
                          'outline'
                        }>
                          {po.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(po.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(po.poDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {po.requestedDelivery ? formatDate(po.requestedDelivery) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
